import type { AbstractAnalyticsDispatcher } from "@asurion-core/eventualize";

import { sanitizeCountryCode } from "@expert/common-ui";
import { useDebouncedCallback } from "@mantine/hooks";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

import type { CustomerVerificationViewProps } from "../CustomerVerificationView";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { useAnalytics } from "../../../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import {
    useCallDetailsStore,
    useVerificationFlowStore,
    useVerifiedCustomerStore,
    useProductOfferStore,
} from "../../../../../stores";
import { useAutoSelectSurvey } from "../customer-info/useAutoSelectSurvey";
import { useCustomerProgram } from "../customer-info/useCustomerProgram";
import { useIVRInfo } from "../customer-info/useIVRInfo";
import { compareName } from "../utils/compareName";
import { computeVerificationUIConfig } from "../utils/computeVerificationUIConfig";
import { useConfirmCustomerVerification } from "./useConfirmCustomerVerification";
import { useCustomerVerificationForm } from "./useCustomerVerificationForm";
import { useGetCustomerDetails } from "./useGetCustomerDetails";
import { useGetCustomerDetailsByBAN } from "./useGetCustomerDetailsByBAN";
import { useIsConfirmationDisabled } from "./useIsConfirmationDisabled";
import { usePreviousSessionSummary } from "./usePreviousSessionSummary";

export interface UseCustomerVerificationParams {
    mdnFromTask: string;
    isPreviousSessionSummaryEnabled: boolean;
}

/**
 * Main orchestrator hook for the customer verification flow.
 * Combines all the smaller hooks and prepares props for CustomerVerificationView.
 */
export function useCustomerVerification(params: UseCustomerVerificationParams): CustomerVerificationViewProps {
    const { mdnFromTask, isPreviousSessionSummaryEnabled } = params;

    const {
        sessionId,
        partner,
        partnerConfig,
        taskType,
        gaiaWebsocket,
        isSimulatedVoiceTask,
        callIntroQuestions,
        features,
    } = useSalesContext();
    const { isVoice, isChat } = taskType;
    const { dispatcher: salesDispatcher } = useAnalytics();
    const dispatcher = salesDispatcher as AbstractAnalyticsDispatcher;

    if (!isVoice && !isChat) {
        throw new Error("Active task is not a supported task type (voice or chat)");
    }

    const isUpdatedCustomerHandoffEnabled = features?.isUpdatedCustomerHandoffEnabled;

    const {
        features: {
            programSelectionEnabled,
            verificationType,
            isInternationalPhoneEnabled = false,
            defaultCountry = "US",
        },
        additionalVerificationFields,
        blocklistedNumbers,
        blocklistedPrefixes,
        survey,
        orderIdMask,
    } = partnerConfig;

    const programs = partnerConfig.programs ?? [];
    const { customerProgram } = useCustomerProgram();
    const useCustomerAgreementProgram = survey?.useCustomerAgreementProgram ?? false;
    const sanitizedDefaultCountry = sanitizeCountryCode(defaultCountry);

    const { customer, isEditingVerificationInput } = useVerifiedCustomerStore((state) =>
        state.getVerifiedCustomer(sessionId),
    );
    const unverifiedCustomerData = useVerifiedCustomerStore((state) => state.getUnverifiedCustomerData(sessionId));
    const { setUnverifiedCustomerData, setIsEditingVerificationInput } = useVerifiedCustomerStore.getState();

    const stepHistory = useVerificationFlowStore((state) => state.getStepHistory(sessionId));
    const currentStep = useVerificationFlowStore((state) => state.getCurrentStep(sessionId));
    const isEditingCustomerInformation = stepHistory.includes("VerificationComplete");

    const latestCustomerData = { ...customer, ...unverifiedCustomerData };
    const mdn = latestCustomerData?.mdn ?? mdnFromTask;
    const customerName = latestCustomerData?.fullName;

    const {
        intent,
        summary,
        conversationalSummary,
        device,
        customerName: customerNameFromIVR,
        orderNumber: orderNumberFromIVR,
        productLink,
    } = useIVRInfo();

    useLayoutEffect(() => {
        const showCustomerHandoffStep = !!conversationalSummary && isUpdatedCustomerHandoffEnabled;
        useVerificationFlowStore
            .getState()
            .setInitialStep(sessionId, showCustomerHandoffStep ? "CustomerHandoff" : "CustomerPhoneInput");
    }, [sessionId, conversationalSummary, isUpdatedCustomerHandoffEnabled]);

    const products = useProductOfferStore((state) => state.getProducts(sessionId));
    const hasProducts = products.length > 0;

    const uiConfig = computeVerificationUIConfig({
        isChat,
        programSelectionEnabled,
        programs,
        useCustomerAgreementProgram,
        additionalVerificationFields,
        verificationType,
        hasProducts,
    });

    useAutoSelectSurvey();

    const [currentSubscriptions, setCurrentSubscriptions] = useState<string[] | undefined>();

    const callDetails = useCallDetailsStore.getState().callDetails;

    const form = useCustomerVerificationForm({
        sessionId,
        verificationType,
        initialMdn: mdn,
        initialEmail: latestCustomerData?.email ?? "",
        initialOrderId: latestCustomerData?.orderId ?? orderNumberFromIVR ?? "",
        initialAccountNumber: latestCustomerData?.accountNumber ?? "",
        showAdditionalDetailsEmailInput: uiConfig.showAdditionalDetailsEmailInput,
        showAdditionalDetailsOrderIdInput: uiConfig.showAdditionalDetailsOrderIdInput,
        showAdditionalDetailsPhoneNumberInput: uiConfig.showAdditionalDetailsPhoneNumberInput,
        orderIdMask,
        blocklistedNumbers,
        blocklistedPrefixes,
        dispatcher,
        shouldSkipMdnValidation: isSimulatedVoiceTask,
    });

    const { addToTimeline: addPrevSummaryToTimeline } = usePreviousSessionSummary({
        sessionId,
        isPreviousSessionSummaryEnabled,
        isChat,
        rootDispatcher: dispatcher,
    });

    const customerDetailsCallback = useCallback(
        (
            result: Parameters<typeof addPrevSummaryToTimeline>[0] extends infer T
                ? { previousSummary?: T; primaryAccountHolder?: { email?: string } }
                : never,
        ) => {
            // Only update the email input if the field is currently empty
            if (
                uiConfig.showAdditionalDetailsEmailInput &&
                form.additionalDetails.getValues().email === "" &&
                result?.primaryAccountHolder?.email != null
            ) {
                form.additionalDetails.setFieldValue("email", result.primaryAccountHolder.email);
            }

            useVerifiedCustomerStore.getState().setHasPrimaryAccountHolder(sessionId, !!result?.primaryAccountHolder);

            addPrevSummaryToTimeline(result.previousSummary);
        },
        [uiConfig.showAdditionalDetailsEmailInput, form, addPrevSummaryToTimeline, sessionId],
    );

    const { data: mdnLookupData, isLoading: isCustomerDetailsByMDNLoading } = useGetCustomerDetails({
        mdn,
        partner,
        dispatcher,
        sessionId,
        customerDetailsCallback,
        verificationType,
        shouldSkipMdnValidation: isSimulatedVoiceTask,
    });

    const {
        data: banLookupData,
        isLoading: isCustomerDetailsByBANLoading,
        refetch: fetchBanLookupData,
    } = useGetCustomerDetailsByBAN({
        billingAccountNumber: form.verification.getValues().billingAccountNumber,
        pin: form.verification.getValues().pin,
        partner,
        dispatcher,
        customerDetailsCallback,
        mdn,
    });

    const customerData = (verificationType === "phoneNumber" ? mdnLookupData : banLookupData) ?? undefined;
    const isCustomerDetailsLoading = isCustomerDetailsByMDNLoading || isCustomerDetailsByBANLoading;

    const { isConfirmationDisabled, reason: reasonConfirmationIsDisabled } = useIsConfirmationDisabled({
        additionalDetailsFormIsValid: form.additionalDetails.isValid(),
        customerName,
        customerProgram,
        isEditingVerificationInput,
        mdn,
        sessionId,
        showProgramSelection: uiConfig.showProgramSelection,
        verificationFormIsValid: form.verification.isValid(),
        verificationType,
        shouldSkipMdnValidation: isSimulatedVoiceTask,
    });

    const { confirmCustomerVerification } = useConfirmCustomerVerification();

    const debouncedSetUnverifiedCustomerData = useDebouncedCallback(
        (sid: string, customerData: Record<string, unknown>) => {
            setUnverifiedCustomerData(sid, customerData);
        },
        { delay: 300, flushOnUnmount: true },
    );

    useEffect(() => {
        if (stepHistory.includes("VerificationComplete")) {
            setIsEditingVerificationInput(sessionId, true);
            setUnverifiedCustomerData(sessionId, { fullName: undefined });
        }
    }, [sessionId, setIsEditingVerificationInput, setUnverifiedCustomerData, stepHistory]);

    const handleConfirmClick = useCallback(async () => {
        if (!form.additionalDetails.isValid() || !gaiaWebsocket) {
            return;
        }

        const additionalDetailsValues = form.additionalDetails.getValues();

        await confirmCustomerVerification({
            sessionId,
            customerName: customerName ?? "",
            mdn,
            mdnFromTask,
            verificationType,
            customerData,
            showAdditionalDetailsEmailInput: uiConfig.showAdditionalDetailsEmailInput,
            showAdditionalDetailsOrderIdInput: uiConfig.showAdditionalDetailsOrderIdInput,
            additionalDetailsEmail: additionalDetailsValues.email,
            additionalDetailsOrderId: additionalDetailsValues.orderId,
            additionalDetailsMdn: additionalDetailsValues.mdn,
            billingAccountNumber: form.verification.getValues().billingAccountNumber,
            currentSubscriptions,
            flushDebouncedUpdates: form.flushDebouncedUpdates,
        });
    }, [
        form,
        gaiaWebsocket,
        sessionId,
        customerName,
        mdn,
        mdnFromTask,
        verificationType,
        customerData,
        uiConfig,
        currentSubscriptions,
        confirmCustomerVerification,
    ]);

    const handleMdnComplete = useCallback(
        (input: string) => {
            setUnverifiedCustomerData(sessionId, {
                mdn: input,
                ...(input !== mdn ? { fullName: undefined } : {}),
            });
            setIsEditingVerificationInput(sessionId, false);
        },
        [sessionId, mdn, setUnverifiedCustomerData, setIsEditingVerificationInput],
    );

    const handleMdnEditClick = useCallback(() => {
        setIsEditingVerificationInput(sessionId, true);
    }, [sessionId, setIsEditingVerificationInput]);

    const handleNameSelect = useCallback(
        (input: string | undefined) => {
            debouncedSetUnverifiedCustomerData(sessionId, { fullName: input });
        },
        [sessionId, debouncedSetUnverifiedCustomerData],
    );

    const handleBanSave = useCallback(() => {
        const didBanOrPinChange = form.verification.isDirty("billingAccountNumber") || form.verification.isDirty("pin");
        setUnverifiedCustomerData(sessionId, {
            accountNumber: form.verification.getValues().billingAccountNumber,
            ...(didBanOrPinChange ? { fullName: undefined } : {}),
        });
        setIsEditingVerificationInput(sessionId, false);
        void dispatcher.dispatchBusinessEvent("BAN_Submitted");
        void fetchBanLookupData();
    }, [form, sessionId, setUnverifiedCustomerData, setIsEditingVerificationInput, dispatcher, fetchBanLookupData]);

    const handleBanEditClick = useCallback(() => {
        setIsEditingVerificationInput(sessionId, true);
    }, [sessionId, setIsEditingVerificationInput]);

    const handleSubscriptionsChange = useCallback((subscriptions: string[] | undefined) => {
        setCurrentSubscriptions(subscriptions);
    }, []);

    const handleCustomerHandoffContinue = useCallback(() => {
        useVerificationFlowStore.getState().pushStep(sessionId, "CustomerPhoneInput");
    }, [sessionId]);

    return {
        sessionId,
        currentStep,

        config: {
            verificationType,
            isInternationalPhoneEnabled,
            defaultCountry: sanitizedDefaultCountry,
            orderIdMask,
            dispatcher,
            blocklistedNumbers,
            blocklistedPrefixes,
            isExpertUniversitySimulatedVoiceTask: isSimulatedVoiceTask,
        },

        customer: {
            data: customerData,
            mdn,
            isLoading: isCustomerDetailsLoading,
            nameFromIVR: customerNameFromIVR,
        },

        editing: {
            isEditingVerificationInput,
            isEditingCustomerInformation,
        },

        visibility: {
            showProgramSelection: uiConfig.showProgramSelection,
            showAdditionalDetailsEmailInput: uiConfig.showAdditionalDetailsEmailInput,
            showAdditionalDetailsOrderIdInput: uiConfig.showAdditionalDetailsOrderIdInput,
            showAdditionalDetailsPhoneNumberInput: uiConfig.showAdditionalDetailsPhoneNumberInput,
        },

        ivr: {
            intent,
            conversationalSummary,
            customerName: customerNameFromIVR,
            summary,
            device,
            productLink,
        },

        verificationForm: {
            getInputProps: form.verification.getInputProps,
            isValid: form.verification.isValid,
            isDirty: form.verification.isDirty,
            getValues: form.verification.getValues,
            setFieldValue: form.verification.setFieldValue,
            key: form.verification.key,
        },

        additionalDetailsForm: {
            getInputProps: form.additionalDetails.getInputProps,
            setFieldValue: form.additionalDetails.setFieldValue,
            key: form.additionalDetails.key,
        },

        confirmation: {
            isDisabled: isConfirmationDisabled,
            disabledReason: reasonConfirmationIsDisabled,
            compareName: () => compareName(customerData, customerName),
        },

        handlers: {
            onConfirmClick: handleConfirmClick,
            onMdnComplete: handleMdnComplete,
            onMdnEditClick: handleMdnEditClick,
            onNameSelect: handleNameSelect,
            onBanSave: handleBanSave,
            onBanEditClick: handleBanEditClick,
            onSubscriptionsChange: handleSubscriptionsChange,
            onCustomerHandoffContinue: handleCustomerHandoffContinue,
        },

        callIntro: {
            partner,
            callDetails,
            questions: callIntroQuestions,
        },

        programs,
    };
}
